/* projectCategories.js — single source of truth for case study categories.
 *
 * Shared by:
 *   • /app/frontend/src/pages/WorkList.jsx                (public filter bar)
 *   • /app/frontend/src/pages/studio/FullProjectEditor.jsx (Studio editor dropdown)
 *   • /app/frontend/src/pages/WorkDetail.jsx               (card badge label)
 *   • /app/backend/project_categories.py                   (validation + migration)
 *
 * Rules:
 *   • `id` is the ONLY value stored in `projects.category`. Never store the
 *     display label. Switching languages or renaming labels must never
 *     break filtering or risk drift.
 *   • "all" is a frontend-only filter pseudo-category — never a project value.
 *   • Order below is the order shown in the UI (both backoffice + public).
 */

export const CATEGORY_IDS = [
    "brand_film",
    "campaign",
    "product_video",
    "social_reels",
    "food_photography",
];

export const CATEGORIES = [
    { id: "brand_film", en: "Brand Film", cn: "品牌影片" },
    { id: "campaign", en: "Campaign", cn: "品牌企划" },
    { id: "product_video", en: "Product Video", cn: "产品影片" },
    { id: "social_reels", en: "Social Reels", cn: "社交短视频" },
    { id: "food_photography", en: "Food Photography", cn: "美食摄影" },
];

export const ALL_FILTER = { id: "all", en: "All", cn: "全部作品" };

export function labelFor(id, lang = "en") {
    const key = lang === "cn" ? "cn" : "en";
    const row = CATEGORIES.find((c) => c.id === id);
    if (row) return row[key];
    // Legacy / unmigrated category — surface the raw stored value so Studio
    // (and anyone auditing the public page) can see what still needs fixing.
    return id || "—";
}

/**
 * normalizeLegacy(raw) — mirror of the backend migration map. We export it
 * so the Studio editor can show "this project is on a legacy category →
 * canonical suggestion is X" without round-tripping to the backend.
 * Returns { id, confident } where confident=false means the mapping is a
 * best guess and must be confirmed in-UI before a save.
 */
export function normalizeLegacy(raw) {
    if (!raw) return { id: null, confident: false };
    const key = String(raw).trim().toLowerCase();
    const CANONICAL = {
        // Brand film family
        "brand film": "brand_film",
        brand: "brand_film",
        "f&b brand": "brand_film",
        "fb brand": "brand_film",
        "brand video": "brand_film",
        "hero film": "brand_film",
        // Campaign family
        campaign: "campaign",
        "brand campaign": "campaign",
        // Product video family
        "product video": "product_video",
        "product film": "product_video",
        // Social reels family
        "social reels": "social_reels",
        reels: "social_reels",
        reel: "social_reels",
        "ig reels": "social_reels",
        tiktok: "social_reels",
        "social media": "social_reels",
        // Food photography family
        "food photography": "food_photography",
        "food photo": "food_photography",
        "food photos": "food_photography",
        "menu photo": "food_photography",
        "menu photos": "food_photography",
        "menu photography": "food_photography",
        photography: "food_photography",
    };
    const AMBIGUOUS = new Set([
        "shop video",
        "menu shoot",
        "branding",
        "shoot",
        "content",
    ]);
    if (CANONICAL[key]) return { id: CANONICAL[key], confident: true };
    if (AMBIGUOUS.has(key)) return { id: null, confident: false, ambiguous: true };
    // Already canonical?
    if (CATEGORY_IDS.includes(key)) return { id: key, confident: true };
    return { id: null, confident: false };
}
